import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  Container,
  Grid,
  Header,
  Segment,
  Button,
  Icon,
  List,
  Card,
  Breadcrumb,
  Dimmer,
  Loader,
  Message,
} from 'semantic-ui-react'
import { fetchIndustryBySlug } from '../../../lib/datocms'
import './Industries.css'

const IndustryDetail = () => {
  const { slug } = useParams()
  const [industry, setIndustry] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadIndustry()
  }, [slug])

  const loadIndustry = async () => {
    try {
      setLoading(true)
      const data = await fetchIndustryBySlug(slug)
      if (!data) {
        setError('Industry not found')
      } else {
        setIndustry(data)
      }
    } catch (err) {
      console.error('Error loading industry:', err)
      setError('Failed to load industry information. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Dimmer active inverted>
        <Loader size='large'>Loading Industry Information...</Loader>
      </Dimmer>
    )
  }

  if (error) {
    return (
      <Container style={{ marginTop: '2em' }}>
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
        <Button as={Link} to='/industries' primary>
          <Icon name='arrow left' /> Back to Industries
        </Button>
      </Container>
    )
  }

  if (!industry) {
    return null
  }

  return (
    <div className="industry-detail-page">
      {/* Breadcrumb */}
      <Container style={{ paddingTop: '20px' }}>
        <Breadcrumb>
          <Breadcrumb.Section as={Link} to='/'>Home</Breadcrumb.Section>
          <Breadcrumb.Divider />
          <Breadcrumb.Section as={Link} to='/industries'>Industries</Breadcrumb.Section>
          <Breadcrumb.Divider />
          <Breadcrumb.Section active>{industry.name}</Breadcrumb.Section>
        </Breadcrumb>
      </Container>

      {/* Hero Section */}
      <Segment className="industry-detail-hero" vertical>
        <Container>
          <Grid columns={2} stackable verticalAlign='middle'>
            <Grid.Column>
              <Header as='h1' inverted>
                {industry.name}
              </Header>
              <p className="industry-hero-description">
                {industry.shortDescription}
              </p>
              <Button 
                primary 
                size='large' 
                as={Link}
                to='/contact-us'
              >
                Get Industry Solution
                <Icon name='arrow right' />
              </Button>
            </Grid.Column>
            <Grid.Column>
              {industry.image?.url && (
                <img 
                  src={industry.image.url} 
                  alt={industry.image.alt || industry.name}
                  style={{ width: '100%', borderRadius: '8px' }}
                />
              )}
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* Description Section */}
      <Segment vertical style={{ padding: '80px 0' }}>
        <Container>
          <Header as='h2' textAlign='center'>Industry Overview</Header>
          <p style={{ fontSize: '1.1em', lineHeight: '1.8' }}>
            {industry.description}
          </p>
        </Container>
      </Segment>

      {/* Benefits Section */}
      {industry.benefits && industry.benefits.length > 0 && (
        <Segment vertical style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
          <Container>
            <Header as='h2' textAlign='center' style={{ marginBottom: '3em' }}>
              Benefits for {industry.name}
            </Header>
            <Grid columns={2} stackable>
              <Grid.Column>
                <List bulleted size='large'>
                  {industry.benefits.slice(0, Math.ceil(industry.benefits.length / 2)).map((benefit, index) => (
                    <List.Item key={index}>{benefit}</List.Item>
                  ))}
                </List>
              </Grid.Column>
              <Grid.Column>
                <List bulleted size='large'>
                  {industry.benefits.slice(Math.ceil(industry.benefits.length / 2)).map((benefit, index) => (
                    <List.Item key={index}>{benefit}</List.Item>
                  ))}
                </List>
              </Grid.Column>
            </Grid>
          </Container>
        </Segment>
      )}

      {/* Use Cases Section */}
      {industry.useCases && industry.useCases.length > 0 && (
        <Segment vertical style={{ padding: '80px 0' }}>
          <Container>
            <Header as='h2' textAlign='center' style={{ marginBottom: '3em' }}>
              Common Use Cases in {industry.name}
            </Header>
            <Grid columns={3} stackable>
              {industry.useCases.map((useCase, index) => (
                <Grid.Column key={index}>
                  <Segment>
                    <Icon name='check circle' color='green' size='large' />
                    <p style={{ marginTop: '10px' }}>{useCase}</p>
                  </Segment>
                </Grid.Column>
              ))}
            </Grid>
          </Container>
        </Segment>
      )}

      {/* Related Industries */}
      {industry.relatedIndustries && industry.relatedIndustries.length > 0 && (
        <Segment vertical style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
          <Container>
            <Header as='h2' textAlign='center' style={{ marginBottom: '3em' }}>
              Related Industries
            </Header>
            <Card.Group itemsPerRow={4} stackable>
              {industry.relatedIndustries.map(related => (
                <Card key={related.id} as={Link} to={`/industries/${related.slug}`}>
                  <Card.Content>
                    {related.icon?.url && (
                      <div className="industry-icon">
                        <img src={related.icon.url} alt={related.icon.alt || related.name} />
                      </div>
                    )}
                    <Card.Header>{related.name}</Card.Header>
                    <Card.Description>{related.shortDescription}</Card.Description>
                  </Card.Content>
                </Card>
              ))}
            </Card.Group>
          </Container>
        </Segment>
      )}

      {/* CTA Section */}
      <Segment className="industry-detail-cta" inverted vertical>
        <Container textAlign='center'>
          <Header as='h2' inverted>
            Transform Your {industry.name} Business
          </Header>
          <p style={{ fontSize: '1.1em', marginBottom: '2em' }}>
            Discover how Trading Systems can revolutionize your operations in the {industry.name.toLowerCase()} industry.
          </p>
          <Button size='large' inverted as={Link} to='/signup'>
            Start Free Trial
          </Button>
          <Button size='large' inverted basic as={Link} to='/contact-us'>
            Request Industry Demo
          </Button>
        </Container>
      </Segment>
    </div>
  )
}

export default IndustryDetail